import { Link } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import type { ConsentChoice, ConsentMode } from '@/lib/analytics/consent'
import { readConsent, writeConsent } from '@/lib/analytics/consent'
import { consentMode } from '@/lib/analytics/consent-mode'
import { applyConsent } from '@/lib/analytics/posthog'

/**
 * Window event that re-opens the notice after a choice has been made. Lets the
 * footer's "Cookie settings" link reach this component from anywhere in the
 * tree without threading a context through the whole app.
 */
export const COOKIE_SETTINGS_EVENT = 'masklabs:cookie-settings'

/** Fires the event above. Exported so the footer doesn't restate the name. */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))
}

/**
 * Short on purpose. The banner's job is to state that non-essential cookies
 * exist and offer the choice; what they actually collect belongs in the
 * Privacy Policy, which is one click away.
 */
// The only difference is the mood of the verb, which is exactly the difference
// in behaviour: in opt-in regions we are asking first and nothing runs until
// Accept; elsewhere it is already running and Decline stops it.
const COPY: Record<ConsentMode, string> = {
  'opt-in': "We'd like to use non-essential cookies.",
  'opt-out': 'We use non-essential cookies.',
}

/**
 * Cookie and tracking notice. Mounted inside `AnalyticsProvider` in
 * `__root.tsx`, so it never appears in an environment with no PostHog key
 * configured (local dev), where the only cookies are strictly necessary ones.
 *
 * Two behaviours, picked by the consent mode the server resolved for this
 * request (see `lib/analytics/consent.ts`):
 *
 * - `opt-in` (EEA/UK): PostHog was initialized with capturing off. This notice
 *   is the only thing that turns it on.
 * - `opt-out` (everywhere else): PostHog is already capturing. This notice says
 *   so and offers the off switch.
 *
 * Renders nothing until after mount: the stored choice lives in localStorage,
 * which does not exist during SSR, so deciding visibility on the first render
 * would mismatch on hydration.
 */
export function CookieNotice() {
  const [mode, setMode] = useState<ConsentMode | null>(null)
  const [open, setOpen] = useState(false)

  // Deliberately does NOT re-apply a stored choice to posthog-js on mount.
  // posthog-js persists the same decision under a CONSENT_VERSION-scoped key
  // (see posthogOptions), so init already honours it — and PostHogProvider
  // calls posthog.init() in ITS effect, which React runs after this child's,
  // so anything touched here would hit an uninitialized instance.
  useEffect(() => {
    setMode(consentMode())
    if (readConsent() === null) setOpen(true)
  }, [])

  useEffect(() => {
    const reopen = () => setOpen(true)
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen)
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen)
  }, [])

  const choose = useCallback((choice: ConsentChoice) => {
    writeConsent(choice)
    applyConsent(choice)
    setOpen(false)
  }, [])

  if (!open || !mode) return null

  return (
    <section
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md"
    >
      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4 shadow-lg">
        <p className="text-sm text-muted-foreground">
          {COPY[mode]}{' '}
          <Link
            to="/privacy"
            className="font-medium text-foreground underline underline-offset-2"
          >
            Privacy Policy
          </Link>
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => choose('granted')}>
            Accept
          </Button>
          <Button size="sm" variant="outline" onClick={() => choose('denied')}>
            Decline
          </Button>
        </div>
      </div>
    </section>
  )
}
